'use client'

import { MoreVert } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { useConfirm } from '~/components/ConfirmDialog'
import { useDeleteConversationMutation, useUpdateConversationMutation } from '~/data/api/client/assistant'
import {
  type AssistantConversation,
  useListConversationPagesInfiniteQuery
} from '~/data/api/client/assistant/enhancedEndpoints'
import { useDateTimeRelativeFormatter } from '~/data/formatters/datetime'
import { logger } from '~/debug/logger'
import { useSnackbarControls } from '~/store/slices/display'

import RenameConversationDialog from './RenameConversationDialog'

type ConversationListProps = {
  activeConversationId?: string
  onSelect: (conversationId: string) => void
  onDeleted: (conversationId: string) => void
}

type ConversationMenu = { anchor: HTMLElement; conversation: AssistantConversation }

/**
 * The organization's conversations for the signed-in user, newest first, a page at a time, each with a menu to rename
 * or delete it.
 */
export default function ConversationList({ activeConversationId, onSelect, onDeleted }: ConversationListProps) {
  const t = useTranslations('features.AssistantV2')
  const formatRelative = useDateTimeRelativeFormatter()
  const { openSnackbar } = useSnackbarControls()
  const { confirm, confirmationDialog } = useConfirm()
  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useListConversationPagesInfiniteQuery()
  const [updateConversation, { isLoading: isRenaming }] = useUpdateConversationMutation()
  const [deleteConversation] = useDeleteConversationMutation()
  const [menu, setMenu] = useState<ConversationMenu | null>(null)
  const [renaming, setRenaming] = useState<AssistantConversation | null>(null)
  const conversations = data?.pages.flatMap((page) => page.data) ?? []
  const now = new Date()

  const startRename = () => {
    setRenaming(menu?.conversation ?? null)
    setMenu(null)
  }

  const rename = async (title: string) => {
    if (!renaming) return
    const conversationId = renaming.id
    try {
      await updateConversation({ conversationId, conversationUpdateParams: { title } }).unwrap()
      // The dialog may have moved on to another conversation while the request was in flight.
      setRenaming((current) => (current?.id === conversationId ? null : current))
    } catch (error) {
      logger.error('Failed to rename an assistant conversation', error)
      openSnackbar(t('renameError'), { severity: 'error' })
    }
  }

  const remove = async () => {
    const conversation = menu?.conversation
    setMenu(null)
    if (!conversation) return
    const confirmed = await confirm({
      title: t('deleteTitle'),
      body: t('deleteBody'),
      confirmLabel: t('delete'),
      cancelLabel: t('cancel'),
      destructive: true
    })
    if (!confirmed) return
    try {
      await deleteConversation({ conversationId: conversation.id }).unwrap()
      onDeleted(conversation.id)
    } catch (error) {
      logger.error('Failed to delete an assistant conversation', error)
      openSnackbar(t('deleteError'), { severity: 'error' })
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} aria-label={t('loading')} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            {t('retry')}
          </Button>
        }
      >
        {t('historyError')}
      </Alert>
    )
  }

  if (conversations.length === 0) {
    return (
      <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
        {t('historyEmpty')}
      </Typography>
    )
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <List dense aria-label={t('history')}>
        {conversations.map((conversation) => (
          <ListItem
            key={conversation.id}
            disablePadding
            secondaryAction={
              <IconButton
                edge="end"
                size="small"
                aria-label={t('conversationMenu')}
                aria-haspopup="menu"
                onClick={(event) => setMenu({ anchor: event.currentTarget, conversation })}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            }
          >
            <ListItemButton
              selected={conversation.id === activeConversationId}
              onClick={() => onSelect(conversation.id)}
            >
              <ListItemText
                primary={conversation.title || t('untitled')}
                secondary={formatRelative(conversation.lastMessageAt ?? conversation.updatedAt, now)}
                slotProps={{ primary: { noWrap: true } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Menu anchorEl={menu?.anchor} open={menu !== null} onClose={() => setMenu(null)}>
        <MenuItem onClick={startRename}>{t('rename')}</MenuItem>
        <MenuItem onClick={() => void remove()} sx={{ color: 'error.main' }}>
          {t('delete')}
        </MenuItem>
      </Menu>
      <RenameConversationDialog
        title={renaming ? renaming.title || t('untitled') : undefined}
        isSaving={isRenaming}
        onSave={(title) => void rename(title)}
        onClose={() => setRenaming(null)}
      />
      {confirmationDialog}
      {hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          <Button size="small" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
            {t('loadMore')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
